import { createConversationMessage, respondToAgentPrompt } from 'services/project-operator/project-operator-requests';
import { createProjectOperatorRunStream } from 'services/project-operator/project-operator-run-stream';
import { createAgentErrorStream, getConversationMessageText } from 'utils';

const ACTIVE_RUN_STATUSES = new Set([
	'queued',
	'provisioning',
	'running',
	'waiting',
	'processing',
	'streaming',
	'submitted',
	'in_progress',
]);

export const isActiveProjectOperatorRun = run => ACTIVE_RUN_STATUSES.has(run?.status);

export const createTerminalProjectOperatorRunStream = () =>
	new ReadableStream({
		start(controller) {
			controller.close();
		},
	});

const getMessageRun = message =>
	message?.run || message?.latestRun || message?.metadata?.run || message?.metadata?.latestRun;

const getActiveRunMessage = messages => {
	return messages.find(
		message => message.role === 'assistant' && ACTIVE_RUN_STATUSES.has(getMessageRun(message)?.status),
	);
};

export const formatRunOutputMessage = (run, inputMessageId = run?.inputMessageId) => {
	const { outputMessage, ...runValues } = run;

	return {
		...outputMessage,
		run: { ...runValues, inputMessageId },
		status: run.status || outputMessage?.status,
	};
};

export const createProjectOperatorTransport = ({
	mode = 'execute',
	project,
	rootApi,
	instance,
	messages = [],
	store,
	getPromptResponse,
	getProjectOperatorSuggestionId,
	getDeliveryMode,
	getIntentOverride,
	getPreflightOverride,
	getPreflightId,
	getReplyToMessageId,
	getTargets,
	getExistingAttachments,
	clearPromptResponse,
	clearInteraction,
	clearPreflight,
	clearProjectOperatorSuggestion,
	clearQueueEditDraft,
	environment = 'sandbox',
	onChunk,
	onStart,
	onSteer,
	onQueue,
	onComment,
	onComplete,
	onError,
	onIntentConfirmation,
}) => {
	const currentBranch = project?.branch;
	if (!currentBranch?.id) throw new Error('Project Operator transport requires an active project branch.');
	const branchRootApi = `${rootApi}/branches/${currentBranch.id}`;

	const sendMessages = async ({ messages: runtimeMessages, abortSignal }) => {
		try {
			const userMessage = [...runtimeMessages].reverse().find(message => message.role === 'user');
			const text = getConversationMessageText(userMessage);
			if (!userMessage || !text) throw new Error('Please enter a message before sending.');

			const promptResponse = getPromptResponse?.();
			const agentSuggestionId = promptResponse ? null : getProjectOperatorSuggestionId?.();
			const runMode = promptResponse?.intent === 'execute' ? 'execute' : mode;
			const activeRun = getActiveRunMessage(messages);

			if (!promptResponse) {
				const { message } = await createConversationMessage({
					rootApi: branchRootApi,
					store,
					text,
					userMessage,
					mode: activeRun ? null : runMode,
					instance: activeRun ? null : instance,
					agentSuggestionId,
					deliveryMode: activeRun ? getDeliveryMode?.() : null,
					promptResponse: null,
					environment,
					intentOverride: getIntentOverride?.(),
					preflightOverride: getPreflightOverride?.(),
					preflightId: getPreflightId?.(),
					replyToMessageId: getReplyToMessageId?.(),
					targets: getTargets?.() || [],
					existingAttachments: getExistingAttachments?.() || [],
				});
				clearQueueEditDraft?.();
				clearInteraction?.();
				if (message.intent === 'comment') {
					onComment?.({ message, text, userMessage });
					return createTerminalProjectOperatorRunStream();
				}
				if (message.metadata?.workspaceHandoff) {
					onComment?.({ message, text, userMessage });
					return createTerminalProjectOperatorRunStream();
				}
				clearPromptResponse?.();
				clearProjectOperatorSuggestion?.();
				if (message.targetRunId) {
					onSteer?.({ message, text, userMessage });
					return createTerminalProjectOperatorRunStream();
				}
				if (message.status === 'pending') {
					onQueue?.({ message, text, userMessage });
					return createTerminalProjectOperatorRunStream();
				}
				const run = message.latestRun;
				if (!run?.id) throw new Error('Message response did not include a target run or latest run.');
				if (!run.outputMessage?.id) throw new Error('Run response did not include an output message.');
				const outputMessage = formatRunOutputMessage(run, message.id);
				const nextRun = { ...run, outputMessage };
				onStart?.({ message, run: nextRun, text, userMessage });
				if (!isActiveProjectOperatorRun(run)) return createTerminalProjectOperatorRunStream();
				return createProjectOperatorRunStream({
					abortSignal,
					message: outputMessage,
					messageId: message.id,
					onChunk,
					onComplete,
					onError,
					rootApi: branchRootApi,
					runId: run.id,
					store,
				});
			}

			const { message, run } = await respondToAgentPrompt({
				rootApi: branchRootApi,
				store,
				text,
				userMessage,
				mode: runMode,
				instance,
				promptResponse,
				environment,
			});
			clearPromptResponse?.();
			clearInteraction?.();
			clearProjectOperatorSuggestion?.();
			if (!run?.id) throw new Error('Run response did not include a run.');
			if (!run.outputMessage?.id) throw new Error('Run response did not include an output message.');
			const outputMessage = formatRunOutputMessage(run, message.id);
			const nextRun = { ...run, outputMessage };
			onStart?.({ message, promptResponse, run: nextRun, text, userMessage });
			if (!isActiveProjectOperatorRun(run)) return createTerminalProjectOperatorRunStream();
			return createProjectOperatorRunStream({
				abortSignal,
				message: outputMessage,
				messageId: message.id,
				onChunk,
				onComplete,
				onError,
				rootApi: branchRootApi,
				runId: run.id,
				store,
			});
		} catch (error) {
			if (
				error?.code === 'conversation_message_intent_confirmation_required' ||
				error?.code === 'operator_preflight_clarification_required'
			) {
				onIntentConfirmation?.({ error, messages: runtimeMessages });
				return createTerminalProjectOperatorRunStream();
			}
			if (error?.code?.startsWith?.('operator_preflight_')) clearPreflight?.();
			clearPromptResponse?.();
			clearProjectOperatorSuggestion?.();
			return createAgentErrorStream(error, onError);
		}
	};

	const reconnectToStream = async ({ abortSignal }: any = {}) => {
		const activeMessage = getActiveRunMessage(messages);
		if (!activeMessage) return null;

		const conversationMessageId = activeMessage.id;
		const activeRun = getMessageRun(activeMessage);
		const projectOperatorRunId = activeRun?.id;
		const projectOperatorRunInputMessageId = activeRun?.inputMessageId;
		if (!conversationMessageId || !projectOperatorRunId || !projectOperatorRunInputMessageId) {
			throw new Error('Active Project Operator run messages require message, run, and input message identifiers.');
		}

		return createProjectOperatorRunStream({
			abortSignal,
			message: { id: conversationMessageId },
			messageId: projectOperatorRunInputMessageId,
			onChunk,
			onComplete,
			onError,
			rootApi: branchRootApi,
			runId: projectOperatorRunId,
			store,
		});
	};

	return { sendMessages, reconnectToStream };
};
